import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import { CookieBanner } from "@/components/site/CookieBanner";
import { MetaPixel } from "@/components/site/MetaPixel";

const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const Grazie = lazy(() => import("@/pages/Grazie"));
const ProtesiFaccette = lazy(() => import("@/pages/ProtesiFaccette"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const TreatmentPage = lazy(() => import("@/pages/TreatmentPage"));
const Testimonianze = lazy(() => import("@/pages/Testimonianze"));
const ChiSiamo = lazy(() => import("@/pages/ChiSiamo"));
const DoveSiamo = lazy(() => import("@/pages/DoveSiamo"));
const Contatti = lazy(() => import("@/pages/Contatti"));
const CityPage = lazy(() => import("@/pages/CityPage"));

const CLARA_AVATAR = "/clara-avatar.jpg";
const CLARA_INVITE = "Scrivi a Clara, la nostra assistente virtuale";
const CLARA_TERMS_1 = "Cliccando su «Accetto», e ogni volta che interagisci con questo assistente virtuale, acconsenti alla registrazione, all'archiviazione e alla condivisione delle tue comunicazioni con fornitori di servizi terzi, come descritto nella ";
const CLARA_TERMS_2 = ". Se non desideri che le tue conversazioni vengano registrate, ti invitiamo a non utilizzare questo servizio.";
const CLARA_LABELS = { "Agree": "Accetto", "Cancel": "Annulla", "Disagree": "Non accetto", "Terms": "Termini di utilizzo", "Terms and conditions": "Termini e condizioni" };

const translateClaraTexts = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    const v = node.nodeValue;
    if (!v) continue;
    if (v.includes('By clicking "Agree,"')) {
      node.nodeValue = v.includes("If you do not wish")
        ? CLARA_TERMS_1 + "Privacy Policy" + CLARA_TERMS_2
        : CLARA_TERMS_1;
    }
    else if (v.includes("If you do not wish")) node.nodeValue = CLARA_TERMS_2;
    else {
      const t = v.trim();
      if (CLARA_LABELS[t]) node.nodeValue = v.replace(t, CLARA_LABELS[t]);
    }
  }
};

const patchClara = (root) => {
  translateClaraTexts(root);
  if (!root.querySelector("style[data-clara-css]")) {
    const st = document.createElement("style");
    st.setAttribute("data-clara-css", "1");
    st.textContent = "@media (max-width:639px){[data-clara-invite]{display:none!important}}";
    root.appendChild(st);
  }
  root.querySelectorAll("div").forEach((d) => {
    if (d.childElementCount !== 0) return;
    const t = d.textContent.trim();
    if (t === "Ti serve aiuto?") {
      d.textContent = CLARA_INVITE;
      d.style.fontWeight = "600";
    }
    if ((t === "Ti serve aiuto?" || t === CLARA_INVITE) && !d.hasAttribute("data-clara-invite")) {
      d.setAttribute("data-clara-invite", "1");
    }
  });
  root.querySelectorAll("canvas").forEach((c) => {
    const holder = c.parentElement;
    if (holder && !holder.dataset.claraPatched) {
      holder.dataset.claraPatched = "1";
      c.style.display = "none";
      holder.style.backgroundImage = `url('${CLARA_AVATAR}')`;
      holder.style.backgroundSize = "cover";
      holder.style.backgroundPosition = "center";
      const wrap = holder.parentElement;
      if (wrap && wrap.className.indexOf("w-9") !== -1) {
        wrap.style.width = "52px";
        wrap.style.height = "52px";
      }
    }
  });
};

const useClaraWidget = () => {
  useEffect(() => {
    let observer;
    let timer;
    let scheduled = false;
    const init = () => {
      const el = document.querySelector("elevenlabs-convai");
      if (!el || !el.shadowRoot) { timer = setTimeout(init, 500); return; }
      patchClara(el.shadowRoot);
      observer = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => { scheduled = false; patchClara(el.shadowRoot); });
      });
      observer.observe(el.shadowRoot, { childList: true, subtree: true, characterData: true });
    };
    init();
    return () => { clearTimeout(timer); observer && observer.disconnect(); };
  }, []);
};

function App() {
  useClaraWidget();
  return (
    <ReactLenis root options={{ lerp: 0.08, duration: 1.4 }}>
      <div className="App">
        <div className="grain-overlay" aria-hidden="true" />
        <Toaster position="top-center" richColors offset={96} />
        <BrowserRouter>
          <MetaPixel />
          <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/grazie" element={<Grazie />} />
            <Route path="/protesi-e-faccette" element={<ProtesiFaccette />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/all-on-4" element={<TreatmentPage />} />
            <Route path="/implantologia-zigomatica" element={<TreatmentPage />} />
            <Route path="/implantologia-iuxtaossea" element={<TreatmentPage />} />
            <Route path="/implantologia-in-carenza-di-osso" element={<TreatmentPage />} />
            <Route path="/rialzo-del-seno-mascellare" element={<TreatmentPage />} />
            <Route path="/carico-immediato-all-on-four" element={<TreatmentPage />} />
            <Route path="/testimonianze" element={<Testimonianze />} />
            <Route path="/chi-siamo" element={<ChiSiamo />} />
            <Route path="/dove-siamo" element={<DoveSiamo />} />
            <Route path="/contatti" element={<Contatti />} />
            <Route path="/impianti-dentali-milano" element={<CityPage />} />
            <Route path="/impianti-dentali-novara" element={<CityPage />} />
            <Route path="/impianti-dentali-torino" element={<CityPage />} />
            <Route path="/impianti-dentali-bergamo" element={<CityPage />} />
            <Route path="/impianti-dentali-palermo" element={<CityPage />} />
          </Routes>
          </Suspense>
          <CookieBanner />
        </BrowserRouter>
      </div>
    </ReactLenis>
  );
}

export default App;
